import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  Clock,
  Check,
  X,
  Plus,
  Save,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  User,
  Info,
} from 'lucide-react';
import {
  subscribeToSchedules,
  upsertSchedule,
  getScheduleByDate,
  getAvailableTimeSlots,
  bulkUpdateSchedules,
  subscribeToBookings,
} from '../../services/firestore';

const ScheduleManagement = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // month or week

  // Time slots configuration
  const [timeSlots, setTimeSlots] = useState([]);
  const [isDateAvailable, setIsDateAvailable] = useState(true);
  const [notes, setNotes] = useState('');

  // Get current month/year for navigation
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Get days in current month
  const daysInMonth = useMemo(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentYear, currentMonth]);

  // Get first day of month
  const firstDayOfMonth = useMemo(() => {
    return new Date(currentYear, currentMonth, 1).getDay();
  }, [currentYear, currentMonth]);

  // Month names in Indonesian
  const monthNames = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];

  const dayNames = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

  // Format date to YYYY-MM-DD
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDateDisplay = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('id-ID', options);
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Load schedules and bookings
  useEffect(() => {
    setLoading(true);

    // Calculate date range for current month view
    const startDate = new Date(currentYear, currentMonth, 1);
    const endDate = new Date(currentYear, currentMonth + 1, 0);

    const startDateStr = formatDateKey(startDate);
    const endDateStr = formatDateKey(endDate);

    const unsubscribeSchedules = subscribeToSchedules(
      (data) => {
        setSchedules(data);
        setLoading(false);
      },
      startDateStr,
      endDateStr
    );

    const unsubscribeBookings = subscribeToBookings((data) => {
      setBookings(data);
    });

    return () => {
      unsubscribeSchedules();
      unsubscribeBookings();
    };
  }, [currentYear, currentMonth]);

  // Get schedule for a specific date
  const getScheduleForDate = (date) => {
    const dateKey = formatDateKey(date);
    return schedules.find(s => s.date === dateKey);
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    const dateKey = formatDateKey(date);
    return bookings.filter(b => {
      const bookingDate = b.eventDate?.toDate ?
        b.eventDate.toDate() :
        new Date(b.eventDate);
      return formatDateKey(bookingDate) === dateKey &&
        !['CANCELLED', 'COMPLETED', 'NO_SHOW'].includes(b.status);
    });
  };

  // Check if date has any bookings
  const hasBookings = (date) => {
    const dateBookings = getBookingsForDate(date);
    return dateBookings.length > 0;
  };

  // Get booking count for date
  const getBookingCount = (date) => {
    return getBookingsForDate(date).length;
  };

  // Handle date click
  const handleDateClick = async (date) => {
    setSelectedDate(date);
    const schedule = getScheduleForDate(date);

    if (schedule) {
      setSelectedSchedule(schedule);
      setTimeSlots(schedule.timeSlots || []);
      setIsDateAvailable(schedule.isAvailable !== false);
      setNotes(schedule.notes || '');
    } else {
      setSelectedSchedule(null);
      // Generate default time slots (9 AM - 8 PM)
      const defaultSlots = [];
      for (let hour = 9; hour <= 20; hour++) {
        defaultSlots.push({
          time: `${hour.toString().padStart(2, '0')}:00`,
          available: true,
        });
      }
      setTimeSlots(defaultSlots);
      setIsDateAvailable(true);
      setNotes('');
    }
  };

  // Save schedule
  const handleSaveSchedule = async () => {
    if (!selectedDate) return;

    setSaving(true);
    try {
      const scheduleData = {
        id: selectedSchedule?.id,
        date: formatDateKey(selectedDate),
        timeSlots: timeSlots,
        isAvailable: isDateAvailable,
        notes: notes,
      };

      await upsertSchedule(scheduleData);

      // Update local state
      setSelectedSchedule(scheduleData);
      alert('Jadwal berhasil disimpan!');
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Gagal menyimpan jadwal. Silakan coba lagi.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle time slot availability
  const toggleTimeSlot = (time) => {
    setTimeSlots(prev =>
      prev.map(slot =>
        slot.time === time ? { ...slot, available: !slot.available } : slot
      )
    );
  };

  // Close modal
  const closeModal = () => {
    setSelectedDate(null);
    setSelectedSchedule(null);
    setTimeSlots([]);
    setNotes('');
  };

  // Generate calendar grid
  const generateCalendarGrid = () => {
    const grid = [];

    // Add empty cells for days before first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
      grid.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      grid.push(new Date(currentYear, currentMonth, day));
    }

    return grid;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF7A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#F7F5F2]">Manajemen Jadwal</h2>
          <p className="text-[#F7F5F2]/70 text-sm mt-1">
            Atur ketersediaan dan slot waktu untuk pemesanan
          </p>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={prevMonth}
            className="p-2 text-[#F7F5F2]/70 hover:text-[#F7F5F2] hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="text-center">
            <h3 className="text-xl font-bold text-[#F7F5F2]">
              {monthNames[currentMonth]} {currentYear}
            </h3>
          </div>

          <button
            onClick={nextMonth}
            className="p-2 text-[#F7F5F2]/70 hover:text-[#F7F5F2] hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex justify-center mb-4">
          <button
            onClick={goToToday}
            className="px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] text-sm font-medium hover:bg-[#D4AF7A]/20"
          >
            Hari Ini
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Day headers */}
          {dayNames.map(day => (
            <div key={day} className="text-center text-sm font-medium text-[#F7F5F2]/60 py-2">
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {generateCalendarGrid().map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const schedule = getScheduleForDate(date);
            const bookingCount = getBookingCount(date);
            const isToday = formatDateKey(date) === formatDateKey(new Date());
            const isPast = date < new Date().setHours(0, 0, 0, 0);
            const isUnavailable = schedule?.isAvailable === false;

            return (
              <button
                key={index}
                onClick={() => !isPast && handleDateClick(date)}
                disabled={isPast}
                className={`aspect-square rounded-lg border p-2 text-left transition-all relative ${
                  isToday
                    ? 'border-[#D4AF7A] bg-[#D4AF7A]/10'
                    : isPast
                    ? 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed'
                    : isUnavailable
                    ? 'border-red-500/30 bg-red-500/10'
                    : 'border-[#D4AF7A]/20 bg-white/5 hover:bg-white/10 hover:border-[#D4AF7A]/50'
                }`}
              >
                <div className="text-sm font-medium text-[#F7F5F2]">
                  {date.getDate()}
                </div>

                {/* Status indicators */}
                <div className="mt-1 space-y-1">
                  {isUnavailable && (
                    <div className="flex items-center gap-1 text-xs text-red-400">
                      <X size={10} />
                      <span className="truncate">Tidak tersedia</span>
                    </div>
                  )}

                  {bookingCount > 0 && (
                    <div className="flex items-center gap-1 text-xs text-[#D4AF7A]">
                      <User size={10} />
                      <span>{bookingCount} booking</span>
                    </div>
                  )}

                  {!isUnavailable && !isPast && schedule && (
                    <div className="flex items-center gap-1 text-xs text-green-400">
                      <Check size={10} />
                      <span className="truncate">Tersedia</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#D4AF7A]/20 text-xs text-[#F7F5F2]/70">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border border-[#D4AF7A] bg-[#D4AF7A]/10 rounded"></div>
            <span>Hari ini</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border border-red-500/30 bg-red-500/10 rounded"></div>
            <span>Tidak tersedia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border border-green-500/30 bg-green-500/10 rounded"></div>
            <span>Tersedia</span>
          </div>
          <div className="flex items-center gap-2">
            <User size={14} />
            <span>Ada booking</span>
          </div>
        </div>
      </div>

      {/* Date Detail Modal */}
      {selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#D4AF7A]/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-[#F7F5F2]">
                    {formatDateDisplay(selectedDate)}
                  </h3>
                  <p className="text-[#F7F5F2]/70 text-sm mt-1">
                    Atur ketersediaan dan slot waktu
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 text-[#F7F5F2]/70 hover:text-[#F7F5F2] hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Availability Toggle */}
              <div className="p-4 bg-white/5 border border-[#D4AF7A]/20 rounded-lg">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-[#D4AF7A]" />
                      <span className="font-medium text-[#F7F5F2]">Tanggal Tersedia</span>
                    </div>
                    <p className="text-[#F7F5F2]/60 text-sm mt-1">
                      Matikan untuk menonaktifkan semua slot pada tanggal ini
                    </p>
                  </div>
                  <button
                    onClick={() => setIsDateAvailable(!isDateAvailable)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      isDateAvailable ? 'bg-[#D4AF7A]' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        isDateAvailable ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>

              {/* Time Slots */}
              {isDateAvailable && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={18} className="text-[#D4AF7A]" />
                    <h4 className="font-semibold text-[#F7F5F2]">Slot Waktu</h4>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {timeSlots.map((slot) => {
                      const booking = getBookingsForDate(selectedDate).find(
                        b => b.eventTime === slot.time
                      );

                      return (
                        <button
                          key={slot.time}
                          onClick={() => !booking && toggleTimeSlot(slot.time)}
                          disabled={!!booking}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            booking
                              ? 'bg-red-500/10 border-red-500/30 cursor-not-allowed'
                              : slot.available
                              ? 'bg-green-500/10 border-green-500/30 text-green-400'
                              : 'bg-white/5 border-[#D4AF7A]/20 text-[#F7F5F2]/50'
                          }`}
                        >
                          <div className="text-sm font-medium">{slot.time}</div>
                          {booking && (
                            <div className="text-xs text-red-400 mt-1">
                              {booking.clientName}
                            </div>
                          )}
                          {!booking && (
                            <div className="text-xs mt-1">
                              {slot.available ? 'Tersedia' : 'Tidak'}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <p className="text-[#F7F5F2]/60 text-xs mt-2">
                    Klik slot untuk mengubah ketersediaan. Slot dengan booking tidak dapat diubah.
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Catatan
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Tambahkan catatan untuk tanggal ini..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                />
              </div>

              {/* Existing Bookings */}
              {getBookingsForDate(selectedDate).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <User size={18} className="text-[#D4AF7A]" />
                    <h4 className="font-semibold text-[#F7F5F2]">Booking pada Tanggal Ini</h4>
                  </div>
                  <div className="space-y-2">
                    {getBookingsForDate(selectedDate).map((booking) => (
                      <div
                        key={booking.id}
                        className="p-3 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-[#F7F5F2]">
                              {booking.clientName}
                            </div>
                            <div className="text-sm text-[#F7F5F2]/70">
                              {booking.eventTime} - {booking.eventType}
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'CONFIRMED' ? 'bg-green-500/20 text-green-400' :
                            booking.status === 'PENDING_DEPOSIT' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {booking.status === 'PENDING_DEPOSIT' ? 'Pending' :
                             booking.status === 'CONFIRMED' ? 'Konfirmasi' :
                             booking.status}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-[#D4AF7A]/20 p-6">
              <div className="flex gap-3">
                <button
                  onClick={handleSaveSchedule}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Simpan Jadwal
                    </>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className="px-4 py-3 bg-white/5 border border-[#D4AF7A]/20 text-[#F7F5F2] rounded-lg hover:bg-white/10"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManagement;
